import { useRef, useCallback } from 'react';
// Import canvas-based confetti implementation
import { createCanvasConfetti } from '../utils/canvasConfetti';
import { VERDICTS } from '../constants';

/**
 * Custom hook for handling Great Insight animations and effects
 * @returns {Object} Animation utilities
 */
export const useGreatInsightAnimation = () => {
  
  /**
   * Trigger badge pop animation and confetti
   * @param {HTMLElement} badge - The badge element to animate
   */
  const triggerBadgeAnimation = useCallback((badge) => {
    // Add pop animation to the badge
    badge.classList.add('badge-pop');
    
    // Create confetti at the badge location - delayed slightly to ensure accurate positioning
    // Log badge information for debugging
    console.log('Badge element:', badge);
    console.log('Badge position:', badge.getBoundingClientRect());
    
    // Small delay to ensure the badge has completed any layout changes
    setTimeout(() => {
      // Use canvas-based confetti implementation
      createCanvasConfetti(document.body, 36, badge); // 10% fewer particles
    }, 100); // 100ms delay helps with more accurate positioning
    
    // Remove the animation class after it completes
    setTimeout(() => {
      badge.classList.remove('badge-pop');
    }, 700);
  }, []);
  
  /**
   * Handle the Great Insight celebration when a new verdict is received
   * @param {string} verdict - The verdict received
   */
  const handleGreatInsightAnimation = useCallback((verdict) => {
    if (verdict !== VERDICTS.GREAT_INSIGHT) return;
    
    // Longer delay to ensure the message is fully rendered and scrolled into view
    setTimeout(() => {
      // Wait for DOM to fully update with verdict badge
      setTimeout(() => {
        // Find the last Great Insight badge using the shimmer animation class
        const greatInsightBadges = document.querySelectorAll('.animate-shimmer');
        
        if (greatInsightBadges.length > 0) {
          // Get the most recent badge
          const badge = greatInsightBadges[greatInsightBadges.length - 1];
          
          // Check if badge is visible in viewport
          const badgeRect = badge.getBoundingClientRect();
          const isVisible = (
            badgeRect.top >= 0 &&
            badgeRect.left >= 0 &&
            badgeRect.bottom <= window.innerHeight &&
            badgeRect.right <= window.innerWidth
          );
          
          if (!isVisible) {
            badge.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setTimeout(() => triggerBadgeAnimation(badge), 300);
          } else {
            triggerBadgeAnimation(badge);
          }
        } else {
          // Fallback to find AI messages if we can't find badge by class
          findAndAnimateBadgeFallback();
        }
      }, 200);
      
      // Add golden glow to container
      const container = document.querySelector('.max-w-xl');
      if (container) {
        container.classList.add('golden-glow');
        setTimeout(() => {
          container.classList.remove('golden-glow');
        }, 3000);
      }
    }, 300);
  }, [triggerBadgeAnimation]);
  
  /**
   * Fallback method to find the badge when animation class doesn't work
   */
  const findAndAnimateBadgeFallback = useCallback(() => {
    const allMessageElements = document.querySelectorAll('.mb-5.animate-fadeIn');
    let lastAIMessage = null;
    
    // Find the most recent AI message
    for (let i = allMessageElements.length - 1; i >= 0; i--) {
      const msgEl = allMessageElements[i];
      if (!msgEl.querySelector('.justify-end')) {
        lastAIMessage = msgEl;
        break;
      }
    }
    
    if (lastAIMessage) {
      // Try to find the badge
      const badge = lastAIMessage.querySelector('[class*="amber"]') || 
                    lastAIMessage.querySelector('.rounded-full');
                    
      if (badge) {
        triggerBadgeAnimation(badge);
      } else {
        // Use the message itself as fallback with canvas-based confetti
        const rect = lastAIMessage.getBoundingClientRect();
        createCanvasConfetti(document.body, 36, { // 10% fewer particles (40 → 36)
          getBoundingClientRect: () => ({
            top: rect.bottom - 30,
            left: rect.left + (rect.width / 2),
            width: 100,
            height: 30
          })
        });
      }
    } else {
      // Final fallback to viewport center
      const viewportCenter = {
        getBoundingClientRect: () => ({
          top: window.innerHeight / 2,
          left: window.innerWidth / 2,
          width: 1,
          height: 1
        })
      };
      createCanvasConfetti(document.body, 36, viewportCenter); // 10% fewer particles (40 → 36)
    }
  }, [triggerBadgeAnimation]);
  
  return {
    handleGreatInsightAnimation
  };
};

export default useGreatInsightAnimation;